/**
 * Composes the whole team profile screen: data, header, next game, tab
 * strip, the two overlays, and the loading, missing and failed states.
 *
 * R8's grid is one responsive container: the next-game card and the tab
 * panel stack below 1024px and sit side by side at 1024px and up, in the
 * same DOM order at every width, with no viewport branch in JavaScript. This
 * screen does not render `SectionTwoPane`: nesting one inside another
 * throws, and `/scores` already provides its own screen shell.
 *
 * This screen does not poll.
 */
import { Skeleton, Tabs, type typeTabItem } from "@sportsmet/ui";
import { useNavigate } from "@tanstack/react-router";
import { type ReactNode } from "react";

import { useOverlay } from "../../routeState/useOverlay";
import { DetailNavBar } from "../scores/DetailNavBar";
import { resolveKickoffLabel } from "../scores/kickoffLabel";
import { TEAM_ALERTS_SHEET, TeamAlertsDialog } from "./TeamAlertsDialog";
import { toTeamGameRowModel } from "./teamGameLogic";
import { TeamHeader } from "./TeamHeader";
import { TeamInjuriesTab } from "./TeamInjuriesTab";
import { TeamNextGameCard } from "./TeamNextGameCard";
import { TeamOverviewTab } from "./TeamOverviewTab";
import { TeamPlayerStatsTab } from "./TeamPlayerStatsTab";
import {
  resolveActiveTeamTab,
  TEAM_PROFILE_BACK_LABEL,
  TEAM_PROFILE_FAILED_LINE,
  TEAM_PROFILE_MISSING_BODY,
  TEAM_PROFILE_MISSING_TITLE,
  TEAM_TAB_LABELS,
  TEAM_TABS_ARIA_LABEL,
  type typeTeamTabKey,
} from "./teamProfileLogic";
import { TeamReadFailed } from "./TeamReadFailed";
import { TeamRosterTab } from "./TeamRosterTab";
import { nextTeamGame } from "./teamScheduleLogic";
import { TeamScheduleTab } from "./TeamScheduleTab";
import {
  TEAM_SIGN_IN_SHEET,
  TeamSignInGateDialog,
} from "./TeamSignInGateDialog";
import { TeamStatsTab } from "./TeamStatsTab";
import { useTeamFollow } from "./useTeamFollow";
import { useTeamProfileData } from "./useTeamProfileData";

// The one top row: Back to Scores, no title. The team's name is the header's
// own h1 (Scores UX spec v1.0 section 8), so the page never stacks "Scores"
// over it.
const TeamBackRow = (): ReactNode => (
  <DetailNavBar
    backLabel={TEAM_PROFILE_BACK_LABEL}
    clearKeys={["teamId", "tab"]}
    testId="team-profile-nav"
  />
);

export const TeamProfileScreen = ({
  teamId,
  tab,
}: {
  teamId: string;
  tab: string | undefined;
}): ReactNode => {
  // No `from` -- same reasoning as `GameDetailScreen.tsx`: this component is
  // rendered under `/scores` today, but pinning `from` would tie it to that
  // one route id and break under the throwaway single-route router
  // `renderInShell` uses in tests.
  const navigate = useNavigate();
  const overlay = useOverlay();
  const profile = useTeamProfileData(teamId);
  const follow = useTeamFollow(teamId);

  if (profile.kind === "loading") {
    return (
      <div className="flex flex-col gap-3">
        <TeamBackRow />
        <Skeleton shape="card" />
        <Skeleton shape="card" />
        <Skeleton shape="card" />
      </div>
    );
  }

  if (profile.kind === "missing") {
    return (
      <>
        <TeamBackRow />
        <div
          data-testid="team-profile-missing"
          className="mx-auto flex max-w-120 flex-col items-center gap-2 py-10 text-center"
        >
          <p className="text-body-strong text-ink">
            {TEAM_PROFILE_MISSING_TITLE}
          </p>
          <p className="text-body text-ink-secondary">
            {TEAM_PROFILE_MISSING_BODY}
          </p>
        </div>
      </>
    );
  }

  if (profile.kind === "failed") {
    return (
      <>
        <TeamBackRow />
        <TeamReadFailed
          layout="page"
          testId="team-profile-failed"
          line={TEAM_PROFILE_FAILED_LINE}
          onRetry={profile.retry}
        />
      </>
    );
  }

  const next = nextTeamGame(profile.games);
  const nextRow =
    next === null
      ? null
      : toTeamGameRowModel({ event: next, teamId, timeZone: profile.timeZone });
  const nextKickoffLabel =
    next === null
      ? ""
      : resolveKickoffLabel({
          status: next.status,
          statusLabel: next.statusLabel,
          kickoffAt: next.kickoffAt,
          timeZone: profile.timeZone,
          nowMs: Date.now(),
        });
  const activeTab = resolveActiveTeamTab(profile.tabs, tab);

  const onOpenGame = (eventId: string): void => {
    void navigate({
      to: ".",
      search: (prev: unknown) => ({
        ...(prev as Record<string, unknown>),
        eventId,
        teamId: undefined,
        tab: undefined,
      }),
    });
  };

  // One branch per key, rather than the two way ternary this was while only two
  // keys existed. That ternary's else arm drew the Schedule for anything that
  // was not Overview, which is how a third key would have rendered the wrong
  // panel under the right label.
  //
  // The `default` assigns to `never`, the way `messages/ChatMembersDialog.tsx`
  // does: add a fourth key to TEAM_TAB_KEYS and this stops compiling, which is
  // the signal to build its panel or to leave the key out of the whitelist
  // deliberately. A silent fallthrough is what this screen just stopped having.
  //
  // `TeamStatsTab` takes the league and fetches for itself. It is not mounted
  // until a fan selects the tab, because `Tabs` unmounts an inactive panel, so
  // a fan who never opens Stats never pays for the league table read.
  const panelFor = (key: typeTeamTabKey): ReactNode => {
    switch (key) {
      case "overview":
        return (
          <TeamOverviewTab
            teamId={teamId}
            games={profile.games}
            timeZone={profile.timeZone}
            onOpenGame={onOpenGame}
            isFailed={profile.isScheduleFailed}
            onRetry={profile.retrySchedule}
          />
        );
      case "schedule":
        return (
          <TeamScheduleTab
            teamId={teamId}
            games={profile.games}
            timeZone={profile.timeZone}
            onOpenGame={onOpenGame}
            isFailed={profile.isScheduleFailed}
            onRetry={profile.retrySchedule}
          />
        );
      case "roster":
        return (
          <TeamRosterTab
            teamId={teamId}
            teamName={profile.header.name}
            teamAbbreviation={profile.header.abbreviation}
          />
        );
      case "playerStats":
        return (
          <TeamPlayerStatsTab
            teamId={teamId}
            teamName={profile.header.name}
            teamAbbreviation={profile.header.abbreviation}
          />
        );
      case "stats":
        return <TeamStatsTab teamId={teamId} leagueId={profile.leagueId} />;
      case "injuries":
        return (
          <TeamInjuriesTab
            teamId={teamId}
            teamName={profile.header.name}
            teamAbbreviation={profile.header.abbreviation}
          />
        );
      default: {
        const unhandled: never = key;
        return unhandled;
      }
    }
  };

  const items: typeTabItem[] = profile.tabs.map((key) => ({
    value: key,
    label: TEAM_TAB_LABELS[key],
    content: panelFor(key),
  }));

  const onToggleFollow = (): void => {
    void follow.toggleFollow().then((outcome) => {
      if (outcome.isSignedOut) {
        overlay.open({ kind: "sheet", name: TEAM_SIGN_IN_SHEET });
        return;
      }
      if (outcome.isFirstFollow) {
        overlay.open({ kind: "sheet", name: TEAM_ALERTS_SHEET });
      }
    });
  };

  return (
    <div data-testid="team-profile">
      <TeamBackRow />
      <TeamHeader
        name={profile.header.name}
        abbreviation={profile.header.abbreviation}
        logoUrl={profile.header.logoUrl}
        recordLabel={profile.header.recordLabel}
        standingLabel={profile.header.standingLabel}
        viewState={follow.viewState}
        isFollowing={follow.isFollowing}
        alertRows={follow.alertRows}
        onToggleFollow={onToggleFollow}
        onOpenAlerts={() => {
          overlay.open({ kind: "sheet", name: TEAM_ALERTS_SHEET });
        }}
      />
      <div className="flex flex-col gap-4 pt-4 lg:flex-row lg:items-start lg:gap-6">
        <div className="lg:w-90 lg:shrink-0">
          <TeamNextGameCard
            row={nextRow}
            kickoffLabel={nextKickoffLabel}
            onOpenGame={onOpenGame}
            isFailed={profile.isScheduleFailed}
            onRetry={profile.retrySchedule}
          />
        </div>
        <div className="lg:min-w-0 lg:flex-1">
          <Tabs
            label={TEAM_TABS_ARIA_LABEL}
            variant="detail"
            items={items}
            value={activeTab}
            onValueChange={(next) => {
              void navigate({
                to: ".",
                search: (prev: unknown) => ({
                  ...(prev as Record<string, unknown>),
                  tab: next,
                }),
              });
            }}
          />
        </div>
      </div>
      <TeamAlertsDialog
        open={overlay.isOpen("sheet", TEAM_ALERTS_SHEET)}
        onOpenChange={(open) => {
          if (!open) {
            overlay.close({ kind: "sheet" });
            follow.clearFirstFollow();
          }
        }}
        teamName={profile.header.name}
        isFirstFollow={follow.isFirstFollow}
        alertRows={follow.alertRows}
        onSetAlert={(alertKindKey, isEnabled) => {
          void follow.setAlert(alertKindKey, isEnabled);
        }}
      />
      <TeamSignInGateDialog
        open={overlay.isOpen("sheet", TEAM_SIGN_IN_SHEET)}
        onOpenChange={(open) => {
          if (!open) overlay.close({ kind: "sheet" });
        }}
      />
    </div>
  );
};
